import React, { useId } from 'react';
import { t } from '@lingui/macro';

import { Heading } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import { SM_MIN } from '@rover/kibble/styles/MediaQuery';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';

import { useSearchByNameExperiment } from './hooks/useSearchByNameExperiment';
import SearchByNameContent from './SearchByNameContent';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialLocation?: string;
};

const SearchByNameOverlay = ({ isOpen, onClose, initialLocation }: Props): JSX.Element | null => {
  const { isEnabled } = useSearchByNameExperiment();
  // Viewport-based (not UA-based) — `@rover/utilities/device.isMobileBrowser`
  // touches `window` at module load and breaks the SSR bundle.
  const isMobile = !useMatchMedia(`(min-width:${SM_MIN}px)`);
  const headerId = useId();
  const headerText = t`Search by name`;

  if (!isEnabled) return null;

  if (isMobile) {
    // BottomSheet has no built-in heading — pass one via HeaderComponent so the
    // dialog has an accessible name from mount (WCAG 4.1.2). Content mounts only
    // while open so search state re-derives from the latest initialLocation.
    return (
      <BottomSheet
        isOpen={isOpen}
        onRequestClose={onClose}
        closeOnDragDown
        isDismissible
        isFullHeight
        headerId={headerId}
        overflowY="auto"
        HeaderComponent={
          <Heading id={headerId} size="400" m="0x">
            {headerText}
          </Heading>
        }
      >
        {isOpen && <SearchByNameContent initialLocation={initialLocation} />}
      </BottomSheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      headerText={headerText}
      headerSize="400"
      showCloseIcon
      headerId={headerId}
      contentProps={{ width: '600px', maxHeight: '80vh', sx: { overflowY: 'auto' } }}
    >
      {isOpen && <SearchByNameContent initialLocation={initialLocation} />}
    </Modal>
  );
};

export default SearchByNameOverlay;
